import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, OnInit, signal, TemplateRef, viewChild } from '@angular/core'
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { ButtonModule } from 'primeng/button'
import { TooltipModule } from 'primeng/tooltip'
import {
  CertificateContractDetailsForm,
  ContractPartyCompany
} from '@features/certificate/models/certificate-contract-details/certificate-contract-details.model'
import { AlbertInputWrapper } from '@features/certificate/components/certificate-input-wrapper/albert-input-wrapper'
import { AlbertCheckboxComponent } from '@shared/components/albert-checkbox/albert-checkbox.component'
import { AlbertTextareaComponent } from '@shared/components/albert-textarea/albert-textarea.component'
import { AlbertInputComponent } from '@shared/components/albert-input/albert-input.component'
import { CertificateBaseComponent } from '@features/certificate/components/certificate-base/certificate-base.component'
import { FurtherInsured } from '@features/certificate/models/certificate-general-info/certificate-general-info.model'
import { MemberService } from '@features/insured/services/member/member.service'

import { AlbertMultiselectComponent } from '@shared/components/albert-multiselect/albert-multiselect.component'
import { hasActualValue, syncFormCategoryState, valueWithChanges } from '@core/utils/form-utils'
import { IfCategoryDirective } from '@features/certificate/directives/if-category.directive'
import { CERTIFICATE_CONTRACT_DETAILS_VISIBILITY } from '@features/certificate/components/certificate-contract-details/certificate-contract-details.visibility'
import { CertificateCategory } from '@shared/models/certificate-category.model'
import { TableModule } from 'primeng/table'
import { InputText } from 'primeng/inputtext'
import { Select } from 'primeng/select'
import { Textarea } from 'primeng/textarea'
import { IftaLabel } from 'primeng/iftalabel'
import { debounceTime, distinctUntilChanged, filter, finalize, map, merge, of, Subscription, switchMap } from 'rxjs'
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop'
import { AiService } from '@services/ai.service'
import { AlbertModalService } from '@services/albert-modal.service'
import { ModalHelperService } from '@services/modal-helper.service'
import { CountryService } from '@services/country.service'
import { GlobalErrorService } from '@services/global-error.service'
import { DEBOUNCE_CONFIG } from '@app/config/DebounceConfig'
import { COMPANY_EXTRACTION_MAX_LENGTH } from '@shared/models/company-extraction.model'
import { formatDisplayDate } from '@core/utils/date-utils'
import {
  emptyContractPartyCompany,
  hasContractPartyChanges,
  isReplacedContractPartiesText,
  mergeExtractedCompanies,
  normalizeContractPartyCompanies,
  sortContractPartyCompanies
} from '@features/certificate/utils/contract-party-companies.util'
import {
  FurtherInsuredLimitsComponent,
  FurtherInsuredPolicyChange
} from '@features/certificate/components/further-insured-limits/further-insured-limits.component'

@Component({
  selector: 'albert-certificate-contract-details',
  imports: [
    AlbertInputWrapper,
    ReactiveFormsModule,
    FormsModule,
    AlbertCheckboxComponent,
    AlbertTextareaComponent,
    ButtonModule,
    AlbertInputComponent,
    AlbertMultiselectComponent,
    TooltipModule,
    IfCategoryDirective,
    TableModule,
    InputText,
    Select,
    Textarea,
    IftaLabel,
    FurtherInsuredLimitsComponent
  ],
  templateUrl: './certificate-contract-details.component.html',
  styleUrl: './certificate-contract-details.component.css',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CertificateContractDetailsComponent extends CertificateBaseComponent implements OnInit {
  public readonly form = input.required<FormGroup<CertificateContractDetailsForm>>()
  /** The insured of the certificate, which cannot be its own further insured. */
  public readonly insuredUuid = input<string | null>(null)
  /** The period of the certificate, whose policies the limits of the further insured are taken from. */
  public readonly periodUuid = input<string | null>(null)
  public readonly selectedFurtherInsured = toSignal(
    toObservable(this.form).pipe(
      switchMap(form => valueWithChanges(form.controls.furtherInsured)),
      map(value => value ?? [])
    ),
    { initialValue: [] }
  )
  public readonly visibility = CERTIFICATE_CONTRACT_DETAILS_VISIBILITY
  public readonly CertificateCategory = CertificateCategory
  public readonly contractPartyRows = signal<ContractPartyCompany[]>([])
  public readonly extractingCompanies = signal(false)
  public readonly formatDisplayDate = formatDisplayDate
  private readonly fetched = signal<FurtherInsured[]>([])
  public readonly options = computed(() =>
    this.mergeSelectedFurtherInsured(this.fetched().filter(item => item.memberId !== this.insuredUuid()))
  )
  private readonly memberService = inject(MemberService)
  private readonly aiService = inject(AiService)
  private readonly modalService = inject(AlbertModalService)
  private readonly modalHelper = inject(ModalHelperService)
  private readonly globalErrorService = inject(GlobalErrorService)
  private readonly countries = toSignal(inject(CountryService).countries$, { initialValue: [] })
  public readonly countryOptions = computed(() =>
    this.countries().map(c => ({ label: c.label, value: c.country }))
  )
  private readonly countryLabels = computed(() => new Map(this.countries().map(c => [c.country, c.label])))
  private readonly debounceTimeMs = inject(DEBOUNCE_CONFIG)
  private readonly euatCheckTemplate = viewChild.required<TemplateRef<unknown>>('euatCheck')
  private readonly furtherInsuredLimitsTemplate = viewChild.required<TemplateRef<unknown>>('furtherInsuredLimits')
  private readonly destroyRef = inject(DestroyRef)
  private extraction: Subscription | null = null

  constructor() {
    super()
    effect(() => {
      const category = this.categoryService.activeCertificateCategory()
      syncFormCategoryState(this.form(), category, this.visibility)
    })
    toObservable(computed(() => ({ form: this.form(), canEdit: this.canEdit() }))).pipe(
      switchMap(state => merge(
        of(false),
        state.form.controls.avn67C.valueChanges.pipe(map(value => value === true))
      ).pipe(map(ticked => ({ ...state, ticked })))),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ form, canEdit, ticked }) => {
      if (canEdit && form.controls.avn67C.value === true) {
        form.controls.designatedContractParty.enable()
      } else {
        form.controls.designatedContractParty.disable()
      }
      if (ticked) {
        form.controls.designatedContractParty.markAsTouched()
      }
    })

    // Emptying the text (e.g. via the wrapper's clear button) drops the recognised companies right away,
    // so both changes land in the same autosave.
    toObservable(this.form).pipe(
      switchMap(form => form.controls.contractParties.valueChanges.pipe(
        filter(value => !hasActualValue(value)),
        map(() => form.controls.contractPartiesEuat),
        filter(companiesCtrl => (companiesCtrl.value ?? []).length > 0)
      )),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(companiesCtrl => {
      companiesCtrl.markAsDirty()
      companiesCtrl.setValue([])
    })

    // Only a new text re-runs the extraction; small edits keep the recognised companies and their EUAT data.
    // Hydration patches keep the control pristine, so loading a certificate never pops the modal open.
    toObservable(this.form).pipe(
      switchMap(form => {
        const contractPartiesCtrl = form.controls.contractParties
        return contractPartiesCtrl.valueChanges.pipe(
          distinctUntilChanged(),
          debounceTime(this.debounceTimeMs),
          filter((value): value is string => hasActualValue(value) && contractPartiesCtrl.dirty && this.canEdit()),
          filter(value => isReplacedContractPartiesText(value, form.controls.contractPartiesEuat.value ?? []))
        )
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.openEuatCheck(true))

    this.destroyRef.onDestroy(() => this.extraction?.unsubscribe())
  }

  public ngOnInit(): void {
    this.loadOptions()
  }

  /**
   * Opens the EUAT Check modal with the saved companies. It never extracts: only a new Contract Parties text does,
   * so countries and EUAT results entered for the companies are not replaced behind the user's back.
   */
  public onOpenEuatCheck(): void {
    this.openEuatCheck(false)
  }

  public onShowFurtherInsuredLimits(): void {
    this.modalService.showContent({
      title: 'Limits of Further Insured',
      type: 'info',
      closable: true,
      styleClass: 'further-insured-limits-dialog',
      contentTemplate: this.furtherInsuredLimitsTemplate(),
      actions: [{ label: 'Close', position: 'right', severity: 'secondary', callback: () => this.modalService.hideContent() }]
    })
  }

  /** Stores the policy chosen in the limits modal with the further insured, so the autosave persists it. */
  public onFurtherInsuredPolicyChange(change: FurtherInsuredPolicyChange): void {
    const control = this.form().controls.furtherInsured
    // Marked dirty first so the autosave, which reacts synchronously to the value change, picks it up.
    control.markAsDirty()
    control.setValue((control.value ?? []).map(item =>
      item.company === change.company ? { ...item, policyId: change.policyId } : item
    ))
  }

  public countryLabel(country: string | null): string {
    return country === null ? '' : this.countryLabels().get(country) ?? country
  }

  public onAddContractPartyRow(): void {
    this.contractPartyRows.update(rows => [...rows, emptyContractPartyCompany()])
  }

  public onRemoveContractPartyRow(row: ContractPartyCompany): void {
    this.contractPartyRows.update(rows => rows.filter(r => r !== row))
  }

  // FIX: Parameter ist jetzt 'unknown' und wird gecastet
  public onSelectionChange(value: unknown): void {
    const typedValue = value as FurtherInsured[]
    this.form().controls.furtherInsured.setValue(typedValue)
  }

  // FIX: Parameter ist jetzt 'unknown' und wird gecastet
  public onFilter(event: unknown): void {
    const typedEvent = event as { filter: string }
    this.loadOptions(typedEvent.filter)
  }

  /**
   * An extraction replaces the saved companies (they no longer belong to the text); the form itself only
   * changes on confirm, so cancelling keeps them.
   */
  private openEuatCheck(extract: boolean): void {
    // Clicking the button blurs the edited text, whose debounced change then arrives while the modal is
    // already open — only (re-)run the extraction instead of showing the modal again.
    if (this.modalService.config()?.contentTemplate === this.euatCheckTemplate()) {
      if (extract) {
        this.contractPartyRows.set([])
        this.extractCompanies()
      }
      return
    }
    // Rows are copied because the table edits them in place; the form only changes on confirm.
    const saved = extract ? [] : this.form().controls.contractPartiesEuat.value ?? []
    this.contractPartyRows.set(sortContractPartyCompanies(saved.map(row => ({ ...row }))))
    this.showEuatCheck()
    if (extract) {
      this.extractCompanies()
    }
  }

  /** Shows the modal for the current {@link contractPartyRows}. */
  private showEuatCheck(): void {
    this.modalService.showContent({
      title: 'EUAT Check',
      type: 'info',
      closable: true,
      // The X behaves like Cancel, so unsaved changes are not dropped without asking.
      onClose: () => this.canEdit() ? this.cancelEuatCheck() : this.closeEuatCheck(),
      styleClass: 'euat-check-dialog',
      focusOnShow: false,
      contentTemplate: this.euatCheckTemplate(),
      actions: this.canEdit()
        ? [
          { label: 'Cancel', position: 'right', severity: 'secondary', outlined: true, callback: () => this.cancelEuatCheck() },
          { label: 'Confirm', position: 'right', severity: 'primary', callback: () => this.confirmEuatCheck() }
        ]
        : [{ label: 'Close', position: 'right', severity: 'secondary', callback: () => this.closeEuatCheck() }]
    })
  }

  private extractCompanies(): void {
    const text = this.form().controls.contractParties.value?.trim() ?? ''
    if (text === '') {
      return
    }
    if (text.length > COMPANY_EXTRACTION_MAX_LENGTH) {
      this.globalErrorService.reportError({
        subsystemName: 'EUAT Check',
        description: `Company names can only be recognised in texts of up to ${COMPANY_EXTRACTION_MAX_LENGTH} characters.`
      })
      return
    }
    this.extraction?.unsubscribe()
    this.extractingCompanies.set(true)
    this.extraction = this.aiService.extractCompanies(text).pipe(
      finalize(() => this.extractingCompanies.set(false))
    ).subscribe({
      next: response => this.contractPartyRows.update(rows => mergeExtractedCompanies(rows, response.companies ?? [])),
      error: () => this.globalErrorService.reportError({
        subsystemName: 'EUAT Check',
        description: 'The company names could not be recognised.'
      })
    })
  }

  /** Writes the rows back to the form, but only when they changed, so an unchanged confirm triggers no autosave. */
  private confirmEuatCheck(): void {
    const control = this.form().controls.contractPartiesEuat
    const rows = this.contractPartyRows()
    if (hasContractPartyChanges(rows, control.value ?? [])) {
      // Marked dirty first so the autosave, which reacts synchronously to the value change, picks it up.
      control.markAsDirty()
      control.setValue(normalizeContractPartyCompanies(rows))
    }
    this.closeEuatCheck()
  }

  /** Closes the modal right away when nothing changed, otherwise asks whether to keep the changes. */
  private cancelEuatCheck(): void {
    if (!hasContractPartyChanges(this.contractPartyRows(), this.form().controls.contractPartiesEuat.value ?? [])) {
      this.closeEuatCheck()
      return
    }
    this.modalHelper.confirmUnsavedChanges().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(decision => {
      switch (decision) {
        case 'save':
          this.confirmEuatCheck()
          break
        case 'discard':
          this.closeEuatCheck()
          break
        case 'cancel':
          // The prompt opened above the EUAT Check, which stays open with the changes kept.
          break
      }
    })
  }

  private closeEuatCheck(): void {
    this.extraction?.unsubscribe()
    this.modalService.hideContent()
  }

  private loadOptions(query?: string): void {
    const category = this.categoryService.activeCertificateCategory()
    if (category === null) {
      return
    }
    const q = hasActualValue(query) ? query : ''
    this.memberService.getParentCompanies(category, q).subscribe(response => {
      this.fetched.set(response.map(item => ({ memberId: item.uuid, company: item.name })))
    })
  }

  private mergeSelectedFurtherInsured(fetched: FurtherInsured[]): FurtherInsured[] {
    // Keyed by company like the multiselect; a selected entry wins, so its member and chosen policy are kept.
    const byCompany = new Map<string, FurtherInsured>()
    for (const item of [...this.selectedFurtherInsured(), ...fetched]) {
      if (!byCompany.has(item.company)) {
        byCompany.set(item.company, item)
      }
    }
    return Array.from(byCompany.values())
  }
}
